import DomainModel from "../../domain-model";
import Signal from "../../control-flow/signal";
import logging from "../../logging";
import { Mesh } from "three";
import { Object3D } from "three/src/core/Object3D";
import { isHighlightable } from "../../view3d/highlightable";

const log = logging('selection');

/**
 */
type Selection = {
    view: Object3D;
    model: DomainModel;
}

export class SelectionManager {
    private _view?: Object3D = null;
    private _model?: DomainModel = null;
    private _multi?: Selection[] = null;

    get view(): Object3D | null {
        return this._view;
    }

    get model(): DomainModel | null {
        return this._model;
    }

    get multi(): Selection[] | null {
        return this._multi;
    }

    /**
     * The onChange signal simply informs the recipient that the selection has
     * changed, but not what it changed from/to. This happens whenever 
     */
    onChange: Signal = new Signal('onChange', 'selection');

    /**
     * Call set to make a single selection that replaces the existing
     * selection. This will clear the highlights of the previously selected
     * items.
     */
    set(view: Object3D, model: DomainModel) {
        log.debug("SelectionManager.set()");

        if(this._view != null && this._model != null) {
            // This is a noop selection, no need to do anything
            if(this._view == view && this._model == model) {
                return;
            }

            clearHighlights(this._view);
        } else if(this._multi != null) {
            for(const item of this._multi) {
                clearHighlights(item.view);
            }
            this._multi = null;
        }

        this._view = view;
        this._model = model;

        setHighlights(view);

        this.onChange.trigger();
    }

    /**
     * Call add to extend an existing selection. This sets the highlight on the
     * new objects and does nothing to the already selected items.
     */
    add(view: Object3D, model: DomainModel) {
        if(this._multi) {
            this._multi.push({view, model});
        } else {
            this._multi = [];
            if(this._view != null && this._model != null) {
                this._multi.push({view: this._view, model: this._model});
                this._view = null;
                this._model = null;
            }
            this._multi.push({view, model});
        }

        setHighlights(view);

        this.onChange.trigger();
    }

    /**
     * This clears the highlights from the selected objects and returns the
     * view, model, and multi properties to null.
     */
    clear() {
        log.debug("SelectionManager.clear()");

        if(this._view != null && this._model != null) {
            clearHighlights(this._view);
            this._view = null;
            this._model = null;
        }
        else if(this._multi != null) {
            for(const item of this._multi) {
                clearHighlights(item.view);
            }
            this._multi = null;
        }

        this.onChange.trigger();
    }
}
export default SelectionManager;

const setHighlights = (view: Object3D) => {
    if(isHighlightable(view)) {
        for(const mesh of view.highlights) {
            if(mesh.userData.isSectionHit) {
                mesh.visible = true;
            }
            // highlight objects go on layer 1 (mask 0b10)
            mesh.layers.set(1);
        }
    }
}

const clearHighlights = (view: Object3D) => {
    if(view && isHighlightable(view)) {
        for(let mesh of view.highlights) {
            if(mesh.userData.isSectionHit) {
                mesh.visible = false;
            }
            // regular objects go on layer 0 (mask 0b01)
            mesh.layers.set(0);
        }
    }
}
